import * as React from 'react'
import { Link } from 'react-router-dom'

import { Box, Icon, InfoDot, Text } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import isEmpty from 'lodash/isEmpty'

import styles from './index.module.scss'

type QueryParams = Record<string, string>

interface ToWithParams {
  pathname: string
  query?: QueryParams
}

export type ToOption = string | ToWithParams

export interface Option {
  title?: string
  href?: string
  target?: string
  hideTargetIcon?: boolean
  type?: 'primary' | 'wrong'
  to?: ToOption
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  onClick?: (event?: Event | React.MouseEvent) => void
  description?: string
  isActive?: boolean
  focus?: boolean
}
interface Props {
  options: Option[]
  onClose?: () => void
  disabled?: boolean
}

const cx = classNames.bind(styles)

const PopoverContent = ({ options, onClose, disabled }: Props) => {
  const onClick = () => {
    onClose && onClose()
  }

  const LinkContent = ({ option }: { option: Option }) => {
    const textColor = option.type === 'wrong' ? 'radicalRed1000' : 'grey800'
    return (
      <div
        className={cx('option', {
          disabled: disabled ? disabled : false,
        })}
      >
        {option.icon && (
          <div className={styles.iconContainer}>
            <Box
              padding="s8"
              borderRadius={{
                all: 'abs016',
              }}
              background="grey400"
            >
              <Icon.Medium icon={option.icon} color={textColor} />
            </Box>
          </div>
        )}
        <div className={styles.content}>
          <Box flexDirection="row" gap="s6" alignItems="center">
            {!!option.title && (
              <Text.Normal weight="medium" color={textColor}>
                {option.title}
              </Text.Normal>
            )}
            {option.focus && <InfoDot size="small" color="radicalRed1000" />}
            {option.target && !option.hideTargetIcon && <Icon.Medium icon="external link" />}
          </Box>
          {!!option.description && (
            <Text.Normal size="200" color={textColor}>
              {option.description}
            </Text.Normal>
          )}
        </div>
      </div>
    )
  }

  const LinkItem = ({ option }: { option: Option }) => {
    if (disabled) return <LinkContent option={option} />

    if (option.href) {
      return (
        <a
          className={styles.link}
          href={option.href}
          onClick={option.onClick}
          rel="nofollow noopener noreferrer"
          target={option.target}
        >
          <LinkContent option={option} />
        </a>
      )
    } else if (option.to) {
      return (
        <Link className={styles.link} to={option.to} onClick={option.onClick}>
          <LinkContent option={option} />
        </Link>
      )
    }

    return (
      <button type="button" className={styles.btn} onClick={option.onClick}>
        <LinkContent option={option} />
      </button>
    )
  }

  const OptionItem = ({ option }: { option: Option }) => {
    if (isEmpty(option)) {
      return <li className={styles.separator} />
    }

    return (
      <li className={styles.item} key={option.title}>
        <LinkItem option={option} />
      </li>
    )
  }

  return (
    <ul role="menu" className={styles.list} onClick={onClick}>
      {options.map((option, index) => (
        <OptionItem key={index} option={option} />
      ))}
    </ul>
  )
}

export default PopoverContent
